import {
  describePresetActionKind,
  formatPresetTimestamp,
  trimString,
} from "./preset-workspace-ui-model.js";
import {
  presetChangeDisplayName,
  presetChangeSignalKey,
  renderPresetChangeValue,
} from "./preset-manager-change-value.js";

let nextHistoryDescriptionId = 1;

function element(tagName, className = "", text = "") {
  const node = document.createElement(tagName);
  node.className = className;
  node.textContent = text;
  return node;
}

function renderValueComparison(change, translate) {
  const comparison = element("span", "mt-1.5 grid gap-0.5 text-xs");
  const row = (label, side) => {
    const line = element("span", "grid min-w-0 grid-cols-[2.25rem_minmax(0,1fr)] items-center gap-1.5");
    line.append(
      element("span", "text-base-content/50", label),
      renderPresetChangeValue(change, side),
    );
    return line;
  };
  comparison.append(
    row(
      translate("presets.history.old", "old:"),
      "old",
    ),
    row(
      translate("presets.history.new", "new:"),
      "new",
    ),
  );
  return comparison;
}

function historyTooltip(node, change, current, translate) {
  const details = [
    formatPresetTimestamp(node.at),
    describePresetActionKind(node.actionKind),
  ].filter(Boolean);
  const signalKey = presetChangeSignalKey(change);
  if (signalKey) {
    details.push(signalKey);
  }
  if (current) {
    details.push(translate("presets.history.current", "Current"));
  }
  return details.join(" · ");
}

function renderHistoryNodeCard(entry, options) {
  const { currentRevisionId, pending, translate } = options;
  const node = entry.node;
  const current = node.revisionId === currentRevisionId;
  if (node.graphBase === true) {
    const anchor = element(
      "div",
      `mx-1.5 my-0.5 flex min-h-9 items-center gap-2 rounded-box border border-dashed px-3 py-1.5 text-xs ${current ? "border-info bg-info/5 text-info" : "border-base-300 bg-base-100 text-base-content/60"}`,
    );
    anchor.dataset.role = "history-base-node";
    anchor.append(
      element(
        "span",
        "truncate font-medium",
        current
          ? translate("presets.history.current", "Current")
          : translate("presets.history.shared_base", "Shared base"),
      ),
    );
    return anchor;
  }

  const change = Array.isArray(node.changes) ? node.changes[0] : null;
  const tooltipText = historyTooltip(node, change, current, translate);
  const tooltipAnchor = element(
    "div",
    "mx-1.5 my-0.5 flex min-w-0 flex-1",
  );
  tooltipAnchor.setAttribute("data-fishy-tooltip", tooltipText);
  const button = element(
    "button",
    `flex min-w-0 flex-1 flex-col rounded-box border px-3 py-2 text-left text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 enabled:hover:border-base-content/40 disabled:cursor-default disabled:opacity-100 ${current ? "border-info bg-info/5" : "border-base-300 bg-base-100"}`,
  );
  button.type = "button";
  button.dataset.action = "use-revision";
  button.dataset.revisionId = node.revisionId;
  button.dataset.role = "history-node";
  button.disabled = pending || current;
  const description = element("span", "sr-only", tooltipText);
  description.id = `fishy-preset-history-description-${nextHistoryDescriptionId}`;
  nextHistoryDescriptionId += 1;
  button.setAttribute("aria-describedby", description.id);

  const heading = element("span", "flex w-full min-w-0 items-center gap-2");
  heading.append(element(
    "span",
    "min-w-0 flex-1 truncate font-semibold",
    (change ? presetChangeDisplayName(change) : trimString(node.summary))
      || describePresetActionKind(node.actionKind),
  ));
  const additionalChanges = Math.max(0, (node.changes?.length ?? 0) - 1);
  if (additionalChanges > 0 || node.changesTruncated === true) {
    heading.append(element(
      "span",
      "shrink-0 text-[0.6875rem] text-base-content/45",
      node.changesTruncated === true ? `+${additionalChanges + 1}…` : `+${additionalChanges}`,
    ));
  }
  button.append(heading);
  if (change) {
    button.append(renderValueComparison(change, translate));
  }
  button.append(description);
  tooltipAnchor.append(button);
  return tooltipAnchor;
}

function renderPassThrough(cell, segment) {
  if (segment?.present !== true) {
    return;
  }
  const line = element(
    "span",
    `absolute inset-y-0 left-1/2 -translate-x-1/2 border-l-2 ${segment.active ? "border-info" : "border-base-content/25"}`,
  );
  line.dataset.segment = "through";
  cell.append(line);
}

function historyGridRow(role, laneCount, heightClass = "") {
  const row = element(
    "li",
    `grid min-w-full grid-flow-col auto-cols-[minmax(10rem,1fr)] ${heightClass}`,
  );
  row.dataset.role = role;
  row.dataset.laneCount = String(laneCount);
  return row;
}

function renderNodeRow(row, graph, options) {
  const li = historyGridRow("history-node-row", graph.laneCount, "items-stretch");
  li.dataset.revisionIds = row.nodes.map((entry) => entry.node.revisionId).join(" ");
  const nodeByLane = new Map(row.nodes.map((entry) => [entry.lane, entry]));
  for (let lane = 0; lane < graph.laneCount; lane += 1) {
    const cell = element("span", "relative flex min-w-0 items-stretch");
    cell.dataset.lane = String(lane);
    const entry = nodeByLane.get(lane);
    if (entry) {
      cell.append(renderHistoryNodeCard(entry, options));
    } else {
      renderPassThrough(cell, row.passThrough[lane]);
    }
    li.append(cell);
  }
  return li;
}

function renderConnectorSegment(cell, position, active) {
  const classes = {
    top: "absolute left-1/2 top-0 h-1/2 -translate-x-1/2 border-l-2",
    bottom: "absolute bottom-0 left-1/2 h-1/2 -translate-x-1/2 border-l-2",
    left: "absolute left-0 top-1/2 w-1/2 -translate-y-1/2 border-t-2",
    right: "absolute right-0 top-1/2 w-1/2 -translate-y-1/2 border-t-2",
  };
  const line = element(
    "span",
    `${classes[position]} ${active ? "border-info" : "border-base-content/25"}`,
  );
  line.dataset.segment = position;
  cell.append(line);
}

function renderConnectorRow(row, graph) {
  const li = historyGridRow("history-connector-row", graph.laneCount, "h-5");
  li.setAttribute("aria-hidden", "true");
  for (let lane = 0; lane < graph.laneCount; lane += 1) {
    const cell = element("span", "relative");
    cell.dataset.lane = String(lane);
    const segment = row.connectorSegments[lane] ?? {};
    for (const position of ["left", "right"]) {
      if (segment[position]) {
        renderConnectorSegment(
          cell,
          position,
          segment[`${position}Active`] === true,
        );
      }
    }
    for (const position of ["top", "bottom"]) {
      if (segment[position]) {
        renderConnectorSegment(
          cell,
          position,
          segment[`${position}Active`] === true,
        );
      }
    }
    li.append(cell);
  }
  return li;
}

export function renderPresetHistoryGraph(graph, options) {
  const fragment = document.createDocumentFragment();
  for (const [index, row] of graph.rows.entries()) {
    fragment.append(renderNodeRow(row, graph, options));
    if (index < graph.rows.length - 1) {
      fragment.append(renderConnectorRow(row, graph));
    }
  }
  return fragment;
}
